/**
 * The order radial (DIRECTION §0.6, 68.1's design; 68.4 at L1, and for lane C's 68.2 at L0): lane E's eight verbs in a
 * ring round a point (the cursor on the table, the crosshair on the ground). Move toward a wedge to light it; a wedge
 * with ▸ (formation, tactics, mark) opens its outer arc after a beat, and moving out over it picks the sub-order.
 * `close()` says what was chosen (or nothing, released in the centre). DOM only; the verbs are core/orderMenu.ts VERBS.
 *
 * 68.2b (lane C): drawn with lane E's icons (ui/commandIcons.ts) in place of the text glyphs; `show` takes options the
 * ground uses — wedges dimmed (a verb that needs a *here* with none: it opens no arc), a banner over the ring (the match
 * paused), a line under it (whom it orders), and the centre's contextual order in place of "release to cancel". At L0
 * the pointer is locked: the host feeds `move` the crosshair plus the mouse's travel since the menu opened.
 */
import { VERBS, wedgeAt, type Verb } from '../core/orderMenu';
import { iconSvg, ICONS, type IconId } from './commandIcons';

/** What the ground's menu adds (all optional; the table passes none). */
export interface RadialOptions {
  /** Verb keys drawn dim, opening no arc (a verb that needs a *here* with none). */
  disabled?: ReadonlySet<string>;
  /** A line over the ring (the match paused while you choose). */
  banner?: string;
  /** A line under the ring (whom it orders). */
  footer?: string;
  /** The centre: the contextual order a release there gives (its icon and words), in place of "release to cancel". */
  hub?: { icon: IconId | null; label: string };
  /** Sub-orders left out this time, as "verb/sub" (63.1n with 67.75: the motor pool's, where the side has none). */
  hidden?: ReadonlySet<string>;
}

/** A verb's or an option's icon (lane E's ids; Mark's "call fire here" is callFire; Loose has none; 69.32 step 2b: the supply drops the crate's). */
function iconFor(verb: string, sub?: string): IconId | null {
  const id = sub === undefined ? verb : sub.startsWith('supplyDrop') ? 'crate' : verb === 'mark' && (sub === 'fire' || sub === 'airstrike' || sub === 'mortar' || sub === 'gunship') ? 'callFire' : sub === 'vehiclesFree' ? 'ride' : sub === 'respawnHere' ? 'beacon' : sub === 'dropship' || sub === 'dropshipHot' || sub === 'helicopter' ? 'reserves' : sub === 'dropshipVehicle' || sub === 'dropshipVehicleHot' ? 'ride' : sub === 'podDrop' ? 'reserves' : sub === 'wavesHere' || sub === 'wavesForward' ? 'beacon' : sub;   // 68.25: vehicles free wears Ride's mark; 69.3b: the dropship the reserve's, its APC Ride's
  return id in ICONS ? id as IconId : null;
}

const R_IN = 34, R_OUT = 104, R_SUB = 172;
const SUB_DELAY_MS = 220;

export class OrderRadial {
  private el: HTMLDivElement;
  private x = 0; private y = 0;
  private wedge: number | null = null;
  private wedgeSince = 0;
  private sub: number | null = null;
  private opts: RadialOptions = {};
  open = false;

  private verbs: Verb[];

  constructor(private readonly all: Verb[] = VERBS) {
    this.verbs = all;
    this.el = document.createElement('div');
    this.el.style.cssText = 'position:fixed; left:0; top:0; width:0; height:0; pointer-events:none; z-index:30; display:none; font:12px system-ui, sans-serif;';
    document.body.appendChild(this.el);
  }

  dispose(): void { this.el.remove(); }

  /** Open at a screen point (the ground's options: see RadialOptions). */
  show(x: number, y: number, opts: RadialOptions = {}): void {
    this.x = x; this.y = y; this.open = true; this.wedge = null; this.sub = null; this.opts = opts;
    const hide = opts.hidden;
    this.verbs = hide?.size ? this.all.map(v => (v.sub ? { ...v, sub: v.sub.filter(s => !hide.has(`${v.key}/${s.key}`)) } : v)) : this.all;
    this.el.style.display = ''; this.el.style.left = `${x}px`; this.el.style.top = `${y}px`;
    this.draw(performance.now());
  }

  /** The cursor moved: light the wedge it points into (and a sub-order out past the ring). */
  move(px: number, py: number): void {
    if (!this.open) return;
    const dx = px - this.x, dy = py - this.y;
    const w = wedgeAt(dx, dy, R_IN, this.verbs.length);
    const now = performance.now();
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (w !== this.wedge && !(this.sub !== null && dist > R_OUT)) { this.wedge = w; this.wedgeSince = now; this.sub = null; }
    const v = this.wedge !== null && !this.opts.disabled?.has(this.verbs[this.wedge].key) ? this.verbs[this.wedge] : null;
    if (v?.sub && now - this.wedgeSince >= SUB_DELAY_MS && dist > R_OUT) {
      // the sub-order nearest the cursor's angle, on the outer arc centred on the wedge
      const pos = this.subPositions(this.wedge!, v.sub.length);
      let best = 0, bd = Infinity;
      pos.forEach((p, i) => { const d = (p.x - dx) ** 2 + (p.y - dy) ** 2; if (d < bd) { bd = d; best = i; } });
      this.sub = best;
    }
    this.draw(now);
  }

  /** 67.70: what a release now would choose (the call's mark is drawn while it is lit), or null. */
  peek(): { verb: string; sub: string | null } | null {
    if (!this.open || this.wedge === null) return null;
    const v = this.verbs[this.wedge];
    return { verb: v.key, sub: v.sub ? (this.sub === null ? null : v.sub[this.sub]?.key ?? null) : null };
  }

  /** Close: the verb chosen and its sub-order, or null (released in the centre). */
  close(): { verb: string; sub: string | null } | null {
    if (!this.open) return null;
    this.open = false;
    this.el.style.display = 'none';
    if (this.wedge === null) return null;
    const v = this.verbs[this.wedge];
    return { verb: v.key, sub: v.sub ? (v.sub[this.sub ?? 0]?.key ?? null) : null };
  }

  private subPositions(wedge: number, n: number): Array<{ x: number; y: number }> {
    const centre = (wedge / this.verbs.length) * 2 * Math.PI;   // clockwise from the top
    const span = Math.min(Math.PI * 0.9, 0.62 * (n - 1));   // about 110 px between choices at R_SUB
    return Array.from({ length: n }, (_, i) => {
      const a = centre + (n === 1 ? 0 : (i / (n - 1) - 0.5) * span);
      return { x: Math.sin(a) * R_SUB, y: -Math.cos(a) * R_SUB };
    });
  }

  private draw(now: number): void {
    const n = this.verbs.length, o = this.opts;
    const hub = o.hub
      ? `<div style="color:${this.wedge === null ? '#e6d070' : '#9aa0b4'}; line-height:1.05;">${o.hub.icon ? iconSvg(o.hub.icon, { size: 22 }) : ''}<div style="font-size:9px;">${o.hub.label}</div></div>`
      : (this.wedge === null ? 'release<br>to cancel' : '');
    const parts: string[] = [`<div style="position:absolute; left:${-R_OUT}px; top:${-R_OUT}px; width:${2 * R_OUT}px; height:${2 * R_OUT}px; border-radius:50%; background:rgba(12,12,24,0.55); border:1px solid rgba(255,255,255,0.15);"></div>`,
      `<div style="position:absolute; left:${-R_IN}px; top:${-R_IN}px; width:${2 * R_IN}px; height:${2 * R_IN}px; border-radius:50%; background:rgba(12,12,24,0.75); border:1px solid ${o.hub && this.wedge === null ? 'rgba(230,208,112,0.7)' : 'rgba(255,255,255,0.2)'}; display:flex; align-items:center; justify-content:center; color:#9aa0b4; font-size:10px; text-align:center;">${hub}</div>`];
    if (o.banner) parts.push(`<div style="position:absolute; left:-120px; top:${-R_OUT - 24}px; width:240px; text-align:center; color:#e9eef5; font-size:10px; letter-spacing:0.12em; text-shadow:0 1px 3px #000;">${o.banner}</div>`);
    if (o.footer) parts.push(`<div style="position:absolute; left:-120px; top:${R_OUT + 8}px; width:240px; text-align:center; color:#c8ccd8; font-size:10px; text-shadow:0 1px 3px #000;">${o.footer}</div>`);
    this.verbs.forEach((v, i) => {
      const a = (i / n) * 2 * Math.PI, r = (R_IN + R_OUT) / 2;
      const x = Math.sin(a) * r, y = -Math.cos(a) * r;
      const lit = i === this.wedge, dim = !!o.disabled?.has(v.key);
      const icon = iconFor(v.key);
      parts.push(`<div style="position:absolute; left:${x - 40}px; top:${y - 22}px; width:80px; text-align:center; color:${lit ? '#ffffff' : '#c8ccd8'}; opacity:${dim ? 0.35 : 1}; ${lit ? 'filter:drop-shadow(0 0 5px #6e9ec2);' : ''}"><div style="display:flex; justify-content:center; line-height:1;">${icon ? iconSvg(icon, { size: lit ? 30 : 26, title: v.label }) : `<span style="font-size:${lit ? 19 : 16}px;">${v.glyph}</span>`}</div><div style="font-size:10px;">${v.label}${v.sub ? ' ▸' : ''}</div></div>`);
    });
    const v = this.wedge !== null && !o.disabled?.has(this.verbs[this.wedge].key) ? this.verbs[this.wedge] : null;
    if (v?.sub && now - this.wedgeSince >= SUB_DELAY_MS) {
      this.subPositions(this.wedge!, v.sub.length).forEach((p, i) => {
        const lit = i === this.sub, icon = iconFor(v.key, v.sub![i].key);
        parts.push(`<div style="position:absolute; left:${p.x - 48}px; top:${p.y - 13}px; width:96px; padding:3px 0; display:flex; align-items:center; justify-content:center; gap:4px; border-radius:10px; background:${lit ? 'rgba(110,158,194,0.9)' : 'rgba(12,12,24,0.8)'}; color:#ffffff; font-size:11px;">${icon ? iconSvg(icon, { size: 16 }) : ''}<span>${v.sub![i].label}</span></div>`);
      });
    }
    this.el.innerHTML = parts.join('');
  }
}
